import React, { useState, useEffect, useRef } from 'react';
import { ANIMATIONS } from '../../../constants/oracleConstants';
import { getRayDisplayName } from '../../../constants/oracleMysticalMappings';
import './CardSelection.css';

const CardSelection = ({ sessionData, selectionType, title, cardCount, onComplete, onBack }) => {
  const [selectedCard, setSelectedCard] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [isRevealing, setIsRevealing] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [cardShuffle, setCardShuffle] = useState([]);
  const timeoutRef = useRef(null);

  // Generate a random shuffle for card positions
  const generateCardShuffle = (count) => {
    const cards = Array.from({ length: count }, (_, i) => i + 1);
    // Fisher-Yates shuffle algorithm
    for (let i = cards.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [cards[i], cards[j]] = [cards[j], cards[i]];
    }
    return cards;
  };


  const getDescription = () => {
    switch (selectionType) {
      case 'diagnosis':
        return 'Estas cartas revelarão os aspectos ocultos da sua situação atual';
      case 'diagnosisElement':
        return 'Escolha o elemento natural que mais ressoa com sua energia neste momento';
      case 'solution':
        return 'Estas cartas mostrarão o caminho para sua transformação';
      case 'solutionElement':
        return 'Selecione o elemento que guiará sua solução';
      default:
        return 'Deixe sua intuição guiá-lo na escolha';
    }
  };

  const getCardTypeLabel = () => {
    return cardCount === 9 ? 'Carta de Tipo' : 'Carta de Elemento';
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    // Don't restore selection automatically - force fresh selection each time
    // This ensures cards are cleared when progressing to next card step
    setSelectedCard(null);
    setIsProcessing(false); // Reset processing state
    // Generate new card shuffle for this selection step
    setCardShuffle(generateCardShuffle(cardCount));
  }, [selectionType, cardCount]); // Reset when selectionType or cardCount changes

  const handleCardClick = (cardNumber) => {
    if (!isRevealing) {
      setSelectedCard(cardNumber);
      setIsRevealing(true);
      
      // Card click registered - console logging removed for production
      
      // Simulate card reveal animation
      timeoutRef.current = setTimeout(() => {
        setIsRevealing(false);
      }, ANIMATIONS.CARD_REVEAL_DURATION);
    }
  };

  const handleContinue = () => {
    if (selectedCard && !isProcessing && cardShuffle.length > 0) {
      setIsProcessing(true);
      // Use the shuffled value instead of the visual position
      const randomizedValue = cardShuffle[selectedCard - 1]; // Convert to 0-based index
      onComplete(randomizedValue);
    }
  };

  const renderCard = (cardNumber) => {
    const isSelected = selectedCard === cardNumber;
    const isHovered = hoveredCard === cardNumber;
    
    return (
      <button
        key={cardNumber}
        className={`oracle-card ${isSelected ? 'selected' : ''} ${isHovered ? 'hovered' : ''}`}
        onClick={() => handleCardClick(cardNumber)}
        onMouseEnter={() => setHoveredCard(cardNumber)}
        onMouseLeave={() => setHoveredCard(null)}
        disabled={isRevealing}
      >
        <div className="card-back">
          <div className="card-pattern">
            <div className="card-symbol">🔮</div>
            <div className="card-energy-lines"></div>
          </div>
        </div>
        
        {isSelected && (
          <div className="card-selection-indicator">
            <div className="selection-glow"></div>
          </div>
        )}
        
        {/* Card number label removed for blank playing card appearance */}
      </button>
    );
  };

  return (
    <div className="oracle-step card-selection-step">
      <h2>{title}</h2>
      <p>{getDescription()}</p>

      <div className="cards-container">
        <div className={`cards-grid cards-${cardCount}`}>
          {Array.from({ length: cardCount }, (_, index) => {
            return renderCard(index + 1);
          })}
        </div>
      </div>

      {/* Confirmation dialog removed - selection is logged to console for development */}

      {/* Show ray context for continuity */}
      <div className="context-reminder">
        <span>{getRayDisplayName(sessionData.raySelection)} • {selectionType.includes('solution') ? 'Fase da Solução' : 'Fase do Diagnóstico'}</span>
      </div>

      <div className="step-navigation">
        <button 
          onClick={handleContinue}
          className="continue-btn primary-cta"
          disabled={!selectedCard || isRevealing || isProcessing}
          style={{ margin: '0 auto' }}
        >
          {isRevealing ? 'Revelando...' : (isProcessing ? 'Processando...' : 'Continuar →')}
        </button>
      </div>
    </div>
  );
};

export default CardSelection;